import { useState, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAppMode } from "@/hooks/useAppMode";
import { useAuthHandlers } from "@/hooks/useAuthHandlers";

type Mode = "login" | "signup" | "forgot" | "verify-otp";

const AppAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, signUp, verifySignupOtp, resendSignupOtp } = useAuth();
  const { provider, isAppMode, isLoading: appModeLoading } = useAppMode();
  const { loading, setLoading, handleLogin, handleForgotPassword } = useAuthHandlers();

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const next = searchParams.get("next") || "/app";
  const brandColor = provider?.brand_primary_color || undefined;
  const displayName = provider?.display_name || provider?.name || "";

  useEffect(() => {
    setStatusMsg(null);
    setOtpCode("");
  }, [mode]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Redirect once authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate(next, { replace: true });
    }
  }, [user, authLoading, navigate, next]);

  if (appModeLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAppMode || !provider) {
    return <Navigate to="/auth" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMsg(null);
    setLoading(true);
    try {
      if (mode === "login") {
        await handleLogin(email, password);
      } else if (mode === "signup") {
        // App signup uses OTP code instead of email link
        const { error } = await signUp(email, password, firstName, lastName, null, true);
        if (error) {
          if (error.message?.includes("User already registered")) {
            throw new Error("An account with this email already exists. Try signing in.");
          }
          throw new Error(error.message);
        }
        setMode("verify-otp");
        setStatusMsg({ type: "success", text: `We sent a 6-digit code to ${email}.` });
        setResendCooldown(30);
      } else if (mode === "verify-otp") {
        if (otpCode.length !== 6) {
          throw new Error("Enter the 6-digit code from your email.");
        }
        const { error } = await verifySignupOtp(email, otpCode);
        if (error) {
          throw new Error(error.message || "Invalid or expired code.");
        }
        // Success — useEffect will navigate when user state updates
      } else {
        const message = await handleForgotPassword(email);
        setStatusMsg({ type: "success", text: message || "Reset email sent. Check your inbox." });
        setMode("login");
      }
    } catch (err) {
      setStatusMsg({ type: "error", text: (err as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setStatusMsg(null);
    setLoading(true);
    try {
      const { error } = await resendSignupOtp(email);
      if (error) throw new Error(error.message);
      setStatusMsg({ type: "success", text: "New code sent to your email." });
      setResendCooldown(30);
    } catch (err) {
      setStatusMsg({ type: "error", text: (err as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const headerText: Record<Mode, string> = {
    login: "Welcome back",
    signup: "Create your account",
    forgot: "Reset your password",
    "verify-otp": "Check your email",
  };

  const subText: Record<Mode, string> = {
    login: `Sign in to book your next session at ${displayName}.`,
    signup: `Join ${displayName} to start booking sessions.`,
    forgot: "Enter your email and we'll send you a reset link.",
    "verify-otp": `Enter the 6-digit code we sent to ${email} to verify your account.`,
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-background"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Branded hero */}
      <div
        className="px-6 pt-10 pb-12 text-white"
        style={{ backgroundColor: brandColor || "hsl(var(--primary))" }}
      >
        <div className="flex items-center gap-3 mb-6">
          {provider.image_url ? (
            <img
              src={provider.image_url}
              alt={displayName}
              className="h-12 w-12 rounded-full object-cover bg-white/10"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-white/15 flex items-center justify-center text-lg font-semibold">
              {displayName.charAt(0)}
            </div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wider opacity-80">App</p>
            <p className="font-semibold">{displayName}</p>
          </div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{headerText[mode]}</h1>
        <p className="text-sm mt-2 opacity-90">{subText[mode]}</p>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
          {mode === "verify-otp" ? (
            <>
              <div className="flex justify-center py-4">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(v) => setOtpCode(v)}
                  autoFocus
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                    <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                    <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                    <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
                    <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
                    <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {statusMsg && (
                <div
                  className={
                    statusMsg.type === "success"
                      ? "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                      : "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  }
                >
                  {statusMsg.text}
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base"
                disabled={loading || otpCode.length !== 6}
                style={brandColor ? { backgroundColor: brandColor } : undefined}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                Didn't get it?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                  className="font-medium text-foreground hover:underline disabled:opacity-50 disabled:no-underline"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>

              <button
                type="button"
                onClick={() => setMode("signup")}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto"
              >
                <ArrowLeft className="h-3 w-3" />
                Use a different email
              </button>
            </>
          ) : (
            <>
              {mode === "signup" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      autoComplete="family-name"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  inputMode="email"
                />
              </div>

              {mode !== "forgot" && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {mode === "login" && (
                      <button
                        type="button"
                        onClick={() => setMode("forgot")}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      autoComplete={mode === "login" ? "current-password" : "new-password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              {statusMsg && (
                <div
                  className={
                    statusMsg.type === "success"
                      ? "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                      : "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  }
                >
                  {statusMsg.text}
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base"
                disabled={loading}
                style={brandColor ? { backgroundColor: brandColor } : undefined}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : mode === "login" ? (
                  "Sign in"
                ) : mode === "signup" ? (
                  "Create account"
                ) : (
                  "Send reset link"
                )}
              </Button>

              <div className="text-center text-sm text-muted-foreground pt-2">
                {mode === "login" && (
                  <>
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("signup")}
                      className="font-medium text-foreground hover:underline"
                    >
                      Sign up
                    </button>
                  </>
                )}
                {mode === "signup" && (
                  <>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="font-medium text-foreground hover:underline"
                    >
                      Sign in
                    </button>
                  </>
                )}
                {mode === "forgot" && (
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="font-medium text-foreground hover:underline"
                  >
                    Back to sign in
                  </button>
                )}
              </div>
            </>
          )}
        </form>

        <p className="text-xs text-muted-foreground text-center mt-8 max-w-md mx-auto">
          By continuing you agree to our{" "}
          <Link to="/terms" className="underline">Terms</Link> and{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
};

export default AppAuth;
